import type { SupabaseClient } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import { commandResultToSyncResult, type DomainCommandResult } from "../domain/commands";
import type { OfflineOperation, SyncTransport, SyncTransportResult } from "./types";

type SourceChannel = "web" | "android" | "ios";

interface RpcErrorLike {
  code?: string;
  message?: string;
  details?: string;
}

function sourceChannel(): SourceChannel {
  const platform = Capacitor.getPlatform();
  if (platform === "android" || platform === "ios") return platform;
  return "web";
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function firstRow(value: unknown): Record<string, unknown> | undefined {
  if (Array.isArray(value)) return record(value[0]);
  if (value && typeof value === "object") return record(value);
  return undefined;
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function optionalNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function optionalBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.length > 0)
    : [];
}

function integerArray(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((item): item is number => typeof item === "number" && Number.isInteger(item))
    : [];
}

function retryableRpcError(error: RpcErrorLike): boolean {
  const message = `${error.code ?? ""} ${error.message ?? ""} ${error.details ?? ""}`.toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("connection") ||
    message.includes("fetch")
  );
}

function rpcFailure(error: RpcErrorLike): SyncTransportResult {
  const code = optionalString(error.code) ?? "SUPABASE_RPC_ERROR";
  if (retryableRpcError(error)) {
    return { kind: "retryable", code };
  }

  return {
    kind: "terminal_conflict",
    code,
    details: {
      ...(error.message ? { message: error.message } : {}),
      ...(error.details ? { details: error.details } : {}),
    },
  };
}

function domainResult(value: unknown, operation: OfflineOperation): DomainCommandResult {
  const row = firstRow(value);
  if (!row) {
    return {
      status: "rejected",
      operationId: operation.operationId,
      aggregateType: "unknown",
      aggregateId: operation.entityId ?? operation.operationId,
      errorCode: "RPC_EMPTY_RESULT",
      result: {},
    };
  }

  const status = optionalString(row.status);
  if (
    status !== "applied" &&
    status !== "replayed" &&
    status !== "terminal_conflict" &&
    status !== "rejected"
  ) {
    return {
      status: "rejected",
      operationId: operation.operationId,
      aggregateType: "unknown",
      aggregateId: operation.entityId ?? operation.operationId,
      errorCode: "RPC_STATUS_INVALID",
      result: row,
    };
  }

  const aggregateVersion = optionalNumber(row.aggregate_version);
  const eventId = optionalString(row.event_id);
  const errorCode = optionalString(row.error_code);

  return {
    status,
    operationId: optionalString(row.operation_id) ?? operation.operationId,
    aggregateType: optionalString(row.aggregate_type) ?? "unknown",
    aggregateId: optionalString(row.aggregate_id) ?? operation.entityId ?? operation.operationId,
    ...(aggregateVersion === null ? {} : { aggregateVersion }),
    ...(eventId ? { eventId } : {}),
    ...(errorCode ? { errorCode } : {}),
    result: record(row.result),
  };
}

function hallPayload(operation: OfflineOperation): Record<string, unknown> {
  const payload = record(operation.payload);
  const details = record(payload.details);
  if (Object.keys(details).length > 0) return details;

  const ignored = new Set([
    "commandName",
    "tankId",
    "tankIds",
    "tankCode",
    "circuitId",
    "stockGroupId",
    "expectedStockVersion",
    "notes",
    "mode",
  ]);

  return Object.fromEntries(Object.entries(payload).filter(([key]) => !ignored.has(key)));
}

export class SupabaseSyncTransport implements SyncTransport {
  constructor(private readonly client: SupabaseClient) {}

  async send(operation: OfflineOperation): Promise<SyncTransportResult> {
    try {
      const rpc = this.rpcFor(operation);
      const { data, error } = await this.client.rpc(rpc.name, rpc.params);

      if (error) return rpcFailure(error);

      return commandResultToSyncResult(domainResult(data, operation));
    } catch (error) {
      const code =
        error instanceof Error && error.message ? error.message.slice(0, 120) : "SYNC_RPC_THROW";

      if (code.startsWith("SYNC_OPERATION_UNSUPPORTED:")) {
        return {
          kind: "terminal_conflict",
          code: "SYNC_OPERATION_UNSUPPORTED",
          details: { operationType: operation.operationType },
        };
      }

      return {
        kind: "retryable",
        code,
      };
    }
  }

  private rpcFor(operation: OfflineOperation): { name: string; params: Record<string, unknown> } {
    const payload = record(operation.payload);
    const channel = sourceChannel();

    if (operation.operationType === "notification.endpoint_register") {
      return {
        name: "notification_endpoint_register_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_endpoint_id: optionalString(payload.endpointId) ?? operation.entityId ?? null,
          target_device_id: optionalString(payload.deviceId) ?? operation.deviceId,
          target_platform: optionalString(payload.platform),
          target_provider: optionalString(payload.provider),
          target_push_token: optionalString(payload.pushToken),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
        },
      };
    }

    if (operation.operationType === "notification.endpoint_set_active") {
      return {
        name: "notification_endpoint_set_active_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_endpoint_id: optionalString(payload.endpointId) ?? operation.entityId ?? null,
          target_is_active: optionalBoolean(payload.isActive),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "water.circuit.upsert") {
      return {
        name: "water_circuit_upsert_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_circuit_id: optionalString(payload.circuitId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_circuit_code: optionalString(payload.circuitCode),
          target_display_name: optionalString(payload.displayName),
          target_circuit_kind: optionalString(payload.circuitKind),
          target_nominal_volume_l: optionalNumber(payload.nominalVolumeL),
          target_is_active: optionalBoolean(payload.isActive),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "water.profile.set") {
      return {
        name: "tank_water_profile_set_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_tank_id: optionalString(payload.tankId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_mode: optionalString(payload.mode),
          target_circuit_id: optionalString(payload.circuitId),
          target_independent_reason: optionalString(payload.independentReason),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "finance.counterparty.upsert") {
      return {
        name: "finance_counterparty_upsert_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_counterparty_id:
            optionalString(payload.counterpartyId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_first_name: optionalString(payload.firstName),
          target_last_name: optionalString(payload.lastName),
          target_phone: optionalString(payload.phone),
          target_note: optionalString(payload.note),
          target_is_customer: optionalBoolean(payload.isCustomer),
          target_is_vendor: optionalBoolean(payload.isVendor),
          target_is_active: optionalBoolean(payload.isActive),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "finance.transaction.record") {
      return {
        name: "finance_transaction_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_transaction_id:
            optionalString(payload.transactionId) ?? operation.entityId ?? null,
          target_initial_payment_id: optionalString(payload.initialPaymentId),
          target_transaction_kind: optionalString(payload.transactionKind),
          target_category_code: optionalString(payload.categoryCode),
          target_description: optionalString(payload.description),
          target_amount_toman: optionalNumber(payload.amountToman),
          target_quantity: optionalNumber(payload.quantity),
          target_unit: optionalString(payload.unit),
          target_tank_id: optionalString(payload.tankId),
          target_counterparty_id: optionalString(payload.counterpartyId),
          target_occurred_on: optionalString(payload.occurredOn),
          target_initial_paid_amount_toman: optionalNumber(payload.initialPaidAmountToman) ?? 0,
          target_initial_payment_method: optionalString(payload.initialPaymentMethod),
          target_payment_note: optionalString(payload.paymentNote),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "finance.payment.record") {
      return {
        name: "finance_payment_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_payment_id: optionalString(payload.paymentId),
          target_transaction_id:
            optionalString(payload.transactionId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_amount_toman: optionalNumber(payload.amountToman),
          target_payment_method: optionalString(payload.paymentMethod),
          target_paid_on: optionalString(payload.paidOn),
          target_note: optionalString(payload.note),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "finance.transaction.void") {
      return {
        name: "finance_transaction_void_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_transaction_id:
            optionalString(payload.transactionId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_reason: optionalString(payload.reason),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "inventory.item.upsert") {
      return {
        name: "inventory_item_upsert_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_item_id: optionalString(payload.itemId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_item_code: optionalString(payload.itemCode),
          target_item_type: optionalString(payload.itemType),
          target_display_name: optionalString(payload.displayName),
          target_feed_product_id: optionalString(payload.feedProductId),
          target_base_unit: optionalString(payload.baseUnit),
          target_reorder_level: optionalNumber(payload.reorderLevel),
          target_is_active: optionalBoolean(payload.isActive),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "inventory.movement.record") {
      return {
        name: "inventory_movement_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_movement_id: optionalString(payload.movementId) ?? operation.entityId ?? null,
          target_item_id: optionalString(payload.itemId),
          target_movement_type: optionalString(payload.movementType),
          target_quantity_delta: optionalNumber(payload.quantityDelta),
          target_unit_cost_toman: optionalNumber(payload.unitCostToman),
          target_finance_transaction_id: optionalString(payload.financeTransactionId),
          target_feeding_event_id: optionalString(payload.feedingEventId),
          target_occurred_on: optionalString(payload.occurredOn),
          target_notes: optionalString(payload.notes),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "asset.upsert") {
      return {
        name: "asset_upsert_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_asset_id: optionalString(payload.assetId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_asset_code: optionalString(payload.assetCode),
          target_asset_name: optionalString(payload.assetName),
          target_category: optionalString(payload.category),
          target_quantity: optionalNumber(payload.quantity),
          target_location_text: optionalString(payload.locationText),
          target_status: optionalString(payload.status),
          target_purchase_value_toman: optionalNumber(payload.purchaseValueToman),
          target_note: optionalString(payload.note),
          target_finance_transaction_id: optionalString(payload.financeTransactionId),
          target_occurred_on: optionalString(payload.occurredOn),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "tank.admin.upsert") {
      return {
        name: "tank_admin_upsert_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_tank_id: optionalString(payload.tankId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_tank_code: optionalString(payload.tankCode),
          target_notes: optionalString(payload.notes),
          target_is_active: optionalBoolean(payload.isActive),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "treatment.protocol.upsert") {
      return {
        name: "treatment_protocol_upsert_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_protocol_id: optionalString(payload.protocolId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_protocol_code: optionalString(payload.protocolCode),
          target_display_name: optionalString(payload.displayName),
          target_route: optionalString(payload.route),
          target_requires_isolation: optionalBoolean(payload.requiresIsolation),
          target_requires_sump_disconnect: optionalBoolean(payload.requiresSumpDisconnect),
          target_manager_approval_required: optionalBoolean(payload.managerApprovalRequired),
          target_protocol_config: record(payload.protocolConfig),
          target_is_active: optionalBoolean(payload.isActive),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "feeding.day_rebuild") {
      return {
        name: "feeding_day_rebuild_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_local_date: optionalString(payload.localDate),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "feeding.policy.publish") {
      return {
        name: "feeding_policy_publish_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_policy_name: optionalString(payload.policyName),
          target_active_weekdays: integerArray(payload.activeWeekdays),
          target_completion_target_minutes: optionalNumber(payload.completionTargetMinutes),
          target_minimum_gap_minutes: optionalNumber(payload.minimumGapMinutes),
          target_round_starts: stringArray(payload.roundStarts),
          target_effective_from: optionalString(payload.effectiveFrom),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "membership.update") {
      return {
        name: "farm_membership_update_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_user_id: optionalString(payload.targetUserId) ?? operation.entityId ?? null,
          target_role: optionalString(payload.role),
          target_is_active: optionalBoolean(payload.isActive),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "permission.override.set") {
      return {
        name: "user_permission_override_set_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_user_id: optionalString(payload.targetUserId) ?? operation.entityId ?? null,
          target_permission_key: optionalString(payload.permissionKey),
          target_decision: optionalBoolean(payload.decision),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "permission.override.remove") {
      return {
        name: "user_permission_override_remove_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_user_id: optionalString(payload.targetUserId) ?? operation.entityId ?? null,
          target_permission_key: optionalString(payload.permissionKey),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "duty.assign") {
      return {
        name: "farm_duty_assignment_set_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_worker_user_id: optionalString(payload.workerUserId),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "breeder.create") {
      return {
        name: "breeder_create_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_breeder_id: optionalString(payload.breederId) ?? operation.entityId ?? null,
          target_breeder_code: optionalString(payload.breederCode),
          target_sex: optionalString(payload.sex),
          target_color: optionalString(payload.color),
          target_visual_description: optionalString(payload.visualDescription),
          target_photo_url: optionalString(payload.photoUrl),
          target_origin_pair_id: optionalString(payload.originPairId),
          target_tank_id: optionalString(payload.targetTankId),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "breeder.update") {
      return {
        name: "breeder_update_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_breeder_id: optionalString(payload.breederId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_breeder_code: optionalString(payload.breederCode),
          target_sex: optionalString(payload.sex),
          target_status: optionalString(payload.status),
          target_color: optionalString(payload.color),
          target_visual_description: optionalString(payload.visualDescription),
          target_photo_url: optionalString(payload.photoUrl),
          target_tank_id: optionalString(payload.targetTankId),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "pair.form_from_stock") {
      return {
        name: "pair_form_from_stock_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_pair_id: optionalString(payload.pairId) ?? operation.entityId ?? null,
          target_male_breeder_id: optionalString(payload.maleBreederId),
          target_female_breeder_id: optionalString(payload.femaleBreederId),
          target_destination_tank_id: optionalString(payload.destinationTankId),
          target_male_source_stock_group_id: optionalString(payload.maleSourceStockGroupId),
          target_male_expected_stock_version: optionalNumber(payload.maleExpectedStockVersion),
          target_male_variety: optionalString(payload.maleVariety),
          target_male_visual_description: optionalString(payload.maleVisualDescription),
          target_female_source_stock_group_id: optionalString(payload.femaleSourceStockGroupId),
          target_female_expected_stock_version: optionalNumber(payload.femaleExpectedStockVersion),
          target_female_variety: optionalString(payload.femaleVariety),
          target_female_visual_description: optionalString(payload.femaleVisualDescription),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "pair.form") {
      return {
        name: "pair_form_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_pair_id: optionalString(payload.pairId) ?? operation.entityId ?? null,
          target_pair_code: optionalString(payload.pairCode),
          target_male_breeder_id: optionalString(payload.maleBreederId),
          target_female_breeder_id: optionalString(payload.femaleBreederId),
          target_tank_id: optionalString(payload.targetTankId),
          target_notes: optionalString(payload.notes),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "pair.move") {
      return {
        name: "pair_move_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_pair_id: optionalString(payload.pairId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_tank_id: optionalString(payload.targetTankId),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "pair.end") {
      return {
        name: "pair_end_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_pair_id: optionalString(payload.pairId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_mode: optionalString(payload.mode),
          target_reason: optionalString(payload.reason),
          target_male_tank_id: optionalString(payload.maleTargetTankId),
          target_female_tank_id: optionalString(payload.femaleTargetTankId),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "breeding.spawn") {
      return {
        name: "breeding_spawn_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_cycle_id: optionalString(payload.cycleId) ?? operation.entityId ?? null,
          target_pair_id: optionalString(payload.pairId),
          target_expected_pair_version:
            optionalNumber(payload.expectedPairVersion) ?? optionalNumber(operation.baseVersion),
          target_expected_tank_id: optionalString(payload.expectedTankId),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "breeding.egg_result") {
      return {
        name: "breeding_egg_result_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_cycle_id: optionalString(payload.cycleId) ?? operation.entityId ?? null,
          target_expected_cycle_version:
            optionalNumber(payload.expectedCycleVersion) ?? optionalNumber(operation.baseVersion),
          target_egg_result: optionalString(payload.eggResult),
          target_hatch_percent: optionalNumber(payload.hatchPercent),
          target_batch_id: optionalString(payload.batchId),
          target_stock_group_id: optionalString(payload.stockGroupId),
          target_fry_quantity: optionalNumber(payload.fryQuantity),
          target_quantity_precision: optionalString(payload.quantityPrecision),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "breeding.free_swim") {
      return {
        name: "breeding_free_swim_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_cycle_id: optionalString(payload.cycleId) ?? operation.entityId ?? null,
          target_expected_cycle_version:
            optionalNumber(payload.expectedCycleVersion) ?? optionalNumber(operation.baseVersion),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "breeding.stage") {
      return {
        name: "breeding_stage_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_cycle_id: optionalString(payload.cycleId) ?? operation.entityId ?? null,
          target_expected_cycle_version:
            optionalNumber(payload.expectedCycleVersion) ?? optionalNumber(operation.baseVersion),
          target_stage: optionalString(payload.stage) ?? optionalString(payload.mode),
          target_failure_reason: optionalString(payload.failureReason),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "stock_group.transfer") {
      return {
        name: "stock_group_transfer_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_stock_group_id: optionalString(payload.stockGroupId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_tank_id: optionalString(payload.targetTankId),
          target_move_quantity: optionalNumber(payload.moveQuantity),
          target_child_stock_group_id: optionalString(payload.childStockGroupId),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "feeding.day_prepare") {
      return {
        name: "feeding_day_prepare_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_local_date: optionalString(payload.localDate),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "feeding.record") {
      return {
        name: "feeding_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_feeding_event_id: optionalString(payload.feedingEventId),
          target_round_tank_id: optionalString(payload.roundTankId),
          target_feed_product_id: optionalString(payload.feedProductId),
          target_amount: optionalNumber(payload.amount),
          target_unit: optionalString(payload.unit),
          target_appetite_level: optionalString(payload.appetiteLevel),
          target_leftover_level: optionalString(payload.leftoverLevel),
          target_notes: optionalString(payload.notes),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType === "task.complete") {
      return {
        name: "hall_task_complete_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_task_id: optionalString(payload.taskId) ?? operation.entityId ?? null,
          target_expected_version:
            optionalNumber(payload.expectedVersion) ?? optionalNumber(operation.baseVersion),
          target_completion_notes: optionalString(payload.completionNotes),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
        },
      };
    }

    if (operation.operationType.startsWith("hall.")) {
      const operationType = operation.operationType.slice("hall.".length);
      const tankIds = stringArray(payload.tankIds);
      const fallbackTank = optionalString(payload.tankId) ?? operation.entityId ?? null;

      return {
        name: "hall_operation_record_v1",
        params: {
          target_operation_id: operation.operationId,
          target_farm_id: operation.farmId,
          target_operation_type: operationType,
          target_tank_ids: tankIds.length > 0 ? tankIds : fallbackTank ? [fallbackTank] : [],
          target_circuit_id: optionalString(payload.circuitId),
          target_stock_group_id: optionalString(payload.stockGroupId),
          target_expected_stock_version:
            optionalNumber(payload.expectedStockVersion) ?? optionalNumber(operation.baseVersion),
          target_performed_at: operation.performedAt,
          target_source_channel: channel,
          target_device_id: operation.deviceId,
          target_payload: hallPayload(operation),
          target_notes: optionalString(payload.notes),
        },
      };
    }

    throw new Error(`SYNC_OPERATION_UNSUPPORTED:${operation.operationType}`);
  }
}
